import { z } from "zod";

export const BASEMAP_FORMAT = "pax-world-v1";
export const BASEMAP_MANIFEST_CONTENT_TYPE = "application/json";
/** A manifest's limit once gzipped, so opening a basemap is always one small fetch. */
export const BASEMAP_MANIFEST_MAX_COMPRESSED_BYTES = 64 * 1024;
/** Every tile is this many pixels across, at every level. */
export const BASEMAP_TILE_PIXELS = 512;
/** The coarsest stored level, where a whole face fits in one tile. */
export const BASEMAP_COARSEST_LEVEL = 9;
/** The finest level the format allows, where pixels are as fine as feature positions. */
export const BASEMAP_FINEST_LEVEL = 32;

/** The mean radius of the Earth, in meters. */
export const EARTH_RADIUS_METERS = 6_371_008.8;

/**
 * Earth's cube orientation: turns a direction on Earth (x toward 0° 0°, z toward the North Pole)
 * into the cube's frame, whose face centers lie on its axes. Every corner is at sea or in a
 * desert, at least 500 km from any town of 15,000 people.
 */
export const EARTH_ORIENTATION = [
  [0.278182213, 0.555945607, -0.783287392],
  [0.342610013, -0.81926649, -0.459805173],
  [-0.897347779, -0.140452483, -0.418377897],
] as const satisfies Orientation;

export const Sha256 = z
  .string()
  .regex(/^[0-9a-f]{64}$/)
  .meta({
    id: "Sha256",
    description: "A SHA-256 digest in lowercase hex. Every published file is named by its own.",
    example: "9f2c5e0b7a41d3c86e15f0a2b9d47c3e8a6f1b0d25c94e7a3f8b16d0c5e2a974",
  });
export type Sha256 = z.infer<typeof Sha256>;

/** A raster's, feature set's, variable's, or expression's name, with its unit in it. */
export const BasemapName = z
  .string()
  .regex(/^[a-z][A-Za-z0-9]{0,63}$/)
  .meta({
    id: "BasemapName",
    description:
      "A camelCase name of up to 64 letters and digits, carrying its unit, such as `elevationMeters`.",
    example: "elevationMeters",
  });

const DetailMap = z.base64url().meta({
  id: "BasemapDetailMap",
  description:
    "Where detail goes deeper than the base level, as a quadtree: one bit a place in pre-order from each face's root, base64url. Above the base level a 1 says detail goes deeper somewhere beneath; from the base level down a 1 says the place is split into four. Empty when no place goes deeper.",
  example: "wA",
});

const Orientation = z
  .tuple([
    z.tuple([z.number(), z.number(), z.number()]),
    z.tuple([z.number(), z.number(), z.number()]),
    z.tuple([z.number(), z.number(), z.number()]),
  ])
  .meta({
    id: "BasemapOrientation",
    description:
      "A rotation matrix, row by row, turning a direction in the world's frame into the cube's frame.",
  });
type Orientation = z.infer<typeof Orientation>;

export const BasemapGeometry = z
  .discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("sphere"),
      radiusMeters: z.number().positive(),
      projection: z.literal("equalAngleCube"),
      orientation: Orientation,
    }),
    z.strictObject({
      kind: z.literal("plane"),
      widthMeters: z.number().positive(),
    }),
  ])
  .meta({
    id: "BasemapGeometry",
    description:
      "A sphere stored on six cube faces through the equal-angle projection, where pixels sit at equal angles across each face, or a plane stored on one square face. Pinned at creation.",
  });
export type BasemapGeometry = z.infer<typeof BasemapGeometry>;

const Ring = z.array(z.tuple([z.number(), z.number()])).min(4);

export const BasemapDisplay = z
  .strictObject({
    flatProjection: z
      .enum(["equalEarth", "mercator", "equirectangular"])
      .optional()
      .meta({ description: "A sphere's default flat projection. Viewers may switch." }),
    coverage: z.array(Ring).min(1).meta({
      description:
        "Rings around where the basemap holds data, in longitude and latitude on a sphere or meters on a plane. Games use it as their default movement bounds.",
    }),
  })
  .meta({ id: "BasemapDisplay" });

const Enlarge = z.enum(["nearest", "bilinear"]).meta({
  id: "BasemapEnlarge",
  description:
    "How readers, the water rule, and sampling read between pixels: each pixel's own value, which viewers draw as rounded squares, or blending between pixel centers.",
});

const Color = z
  .string()
  .regex(/^#[0-9a-f]{6}$/)
  .meta({ id: "BasemapColor", description: "An sRGB color in lowercase hex.", example: "#79c1f4" });

const Legend = z
  .record(
    z.string().regex(/^(25[0-4]|2[0-4]\d|1\d\d|[1-9]?\d)$/),
    z.strictObject({
      name: z.string().min(1).max(64),
      light: Color.optional().meta({ description: "The id's color in the light theme." }),
      dark: Color.optional().meta({ description: "The id's color in the dark theme." }),
      weight: z.number().positive().optional().meta({
        description:
          "How much the id counts when coarser levels are derived. Towns at 4 survive zooming out.",
      }),
    }),
  )
  .meta({
    id: "BasemapLegend",
    description: "Each category id from 0 to 254, its name, and the colors editors show it in.",
  });

const bundle = BasemapName.meta({
  description: "The raster resolution bundle whose tile file holds it.",
});

export const BasemapColorEncoding = z
  .discriminatedUnion("codec", [
    z.strictObject({ codec: z.literal("avif"), quality: z.int().min(0).max(100) }),
    z.strictObject({ codec: z.literal("webpLossless") }),
  ])
  .meta({
    id: "BasemapColorEncoding",
    description:
      "How a color raster's tiles are encoded: AVIF at a quality, with full-resolution 8-bit color, or lossless WebP.",
    example: { codec: "avif", quality: 80 },
  });
export type BasemapColorEncoding = z.infer<typeof BasemapColorEncoding>;

const colorRaster = z.strictObject({
  type: z.literal("color"),
  colorSpace: z.literal("srgb"),
  encoding: BasemapColorEncoding,
  enlarge: Enlarge.default("bilinear"),
});
const categoryRaster = z.strictObject({ type: z.literal("category"), legend: Legend });
const numberRaster = z.strictObject({
  type: z.literal("number"),
  step: z.number().positive(),
  enlarge: Enlarge.default("bilinear"),
});
const elevationRaster = z.strictObject({
  type: z.literal("elevation"),
  step: z.number().positive(),
  enlarge: Enlarge.default("bilinear"),
});

const rasterDescription =
  "One named quantity with a value at every pixel. Numbers and elevation are stored as counts of `step`; categories are always enlarged with `nearest`.";

/** What a raster's values mean, as a draft holds it before publishing bundles it. */
export const BasemapRasterRules = z
  .discriminatedUnion("type", [colorRaster, categoryRaster, numberRaster, elevationRaster])
  .meta({ id: "BasemapRasterRules", description: rasterDescription });
export type BasemapRasterRules = z.infer<typeof BasemapRasterRules>;

export const BasemapRaster = z
  .discriminatedUnion("type", [
    colorRaster.extend({ bundle }),
    categoryRaster.extend({ bundle }),
    numberRaster.extend({ bundle }),
    elevationRaster.extend({ bundle }),
  ])
  .meta({ id: "BasemapRaster", description: rasterDescription });
export type BasemapRaster = z.infer<typeof BasemapRaster>;

export const BasemapVariable = z
  .discriminatedUnion("type", [
    z.strictObject({
      type: z.literal("float64"),
      min: z.number(),
      max: z.number(),
      step: z.number().positive().optional(),
      default: z.number(),
      wraps: z.boolean().default(false),
      label: z.string().min(1).max(40),
    }),
    z.strictObject({
      type: z.literal("int32"),
      min: z.int(),
      max: z.int(),
      step: z.int().positive().optional(),
      default: z.int(),
      wraps: z.boolean().default(false),
      label: z.string().min(1).max(40),
    }),
    z.strictObject({
      type: z.literal("boolean"),
      default: z.boolean(),
      label: z.string().min(1).max(40),
    }),
    z.strictObject({
      type: z.literal("category"),
      legend: Legend,
      default: z.int().min(0).max(254),
      label: z.string().min(1).max(40),
    }),
  ])
  .meta({
    id: "BasemapVariable",
    description: "One value for the whole world, and the only part of a basemap a game can change.",
  });
export type BasemapVariable = z.infer<typeof BasemapVariable>;

const waterBodyDescription =
  "Water whose level is a variable. Its reach, the lowest level at which its water covers each pixel, is made by the editor's water body tool.";

export const BasemapWaterBodyRules = z
  .strictObject({
    level: BasemapName.meta({ description: "The variable holding the body's level." }),
  })
  .meta({ id: "BasemapWaterBodyRules", description: waterBodyDescription });

const WaterBody = BasemapWaterBodyRules.extend({
  bundle: BasemapName.meta({ description: "The bundle whose tiles hold the body's reach." }),
}).meta({ id: "BasemapWaterBody", description: waterBodyDescription });

const FeatureProperty = z
  .discriminatedUnion("type", [
    z.strictObject({ name: BasemapName, type: z.enum(["string", "int32", "float64", "boolean"]) }),
    z.strictObject({ name: BasemapName, type: z.literal("category"), legend: Legend }),
  ])
  .meta({ id: "BasemapFeatureProperty" });

export const BasemapFeatureSetRules = z
  .strictObject({
    geometry: z.enum(["line", "polygon"]),
    properties: z.array(FeatureProperty).max(64),
    importance: BasemapName.optional().meta({
      description:
        "A numeric property that decides which features coarser tiles keep when they would exceed their byte budget.",
    }),
  })
  .meta({
    id: "BasemapFeatureSetRules",
    description: "Features of one geometry type and one list of properties.",
  });
export type BasemapFeatureSetRules = z.infer<typeof BasemapFeatureSetRules>;

export const BasemapFeatureSet = BasemapFeatureSetRules.extend({
  tileFile: Sha256,
  detailMap: DetailMap,
}).meta({
  id: "BasemapFeatureSet",
  description:
    "Features of one geometry type and one list of properties, clipped into one tile file that is exact at its finest level.",
});
export type BasemapFeatureSet = z.infer<typeof BasemapFeatureSet>;

/** The most annotations a basemap holds. */
export const BASEMAP_MAX_ANNOTATIONS = 1000;
/** The most positions one annotation's geometry holds. */
export const BASEMAP_MAX_ANNOTATION_POSITIONS = 1000;

const Position = z.tuple([z.number(), z.number()]).meta({
  id: "BasemapPosition",
  description: "Longitude and latitude in degrees on a sphere, or meters across a plane.",
});

const AnnotationGeometry = z
  .discriminatedUnion("type", [
    z.strictObject({ type: z.literal("point"), coordinates: Position }),
    z.strictObject({
      type: z.literal("line"),
      coordinates: z.array(Position).min(2).max(BASEMAP_MAX_ANNOTATION_POSITIONS),
    }),
    z.strictObject({
      type: z.literal("polygon"),
      coordinates: z
        .array(z.array(Position).min(4))
        .min(1)
        .refine(
          rings => rings.flat().length <= BASEMAP_MAX_ANNOTATION_POSITIONS,
          `A polygon holds at most ${BASEMAP_MAX_ANNOTATION_POSITIONS} positions.`,
        )
        .meta({
          description:
            "Closed rings with their inside on the left: the outer ring counterclockwise, then any holes clockwise.",
        }),
    }),
  ])
  .meta({
    id: "BasemapAnnotationGeometry",
    description: "A point, a line, or a polygon. Edges are great-circle arcs on a sphere.",
  });

export const BasemapAnnotation = z
  .strictObject({
    id: z.uuid(),
    name: z.string().min(1).max(120),
    geometry: AnnotationGeometry,
    properties: z
      .record(BasemapName, z.union([z.string().max(1000), z.number(), z.boolean()]))
      .refine(properties => Object.keys(properties).length <= 64, "At most 64 properties."),
  })
  .meta({
    id: "BasemapAnnotation",
    description:
      "A named place, route, or region that games and the game master can look up. Never drawn.",
  });
export type BasemapAnnotation = z.infer<typeof BasemapAnnotation>;

export const BasemapAnnotations = z
  .array(BasemapAnnotation)
  .max(BASEMAP_MAX_ANNOTATIONS)
  .refine(
    annotations =>
      new Set(annotations.map(annotation => annotation.id)).size === annotations.length,
    "Each annotation has its own id.",
  )
  .meta({ id: "BasemapAnnotations" });

const Bundle = z
  .strictObject({
    tileFile: Sha256,
    finestLevel: z.int().min(9).max(32).meta({
      description: "The level every place is stored down to, unless the detail map goes deeper.",
    }),
    detailMap: DetailMap,
  })
  .meta({
    id: "BasemapBundle",
    description: "Rasters stored together in one tile file, one section per raster in each tile.",
  });

export const BasemapExpression = z.json().meta({
  id: "BasemapExpression",
  description:
    'A declarative expression, such as `["get", "cover"]`, that renderers compile to shaders.',
});

const Appearance = z
  .strictObject({
    color: BasemapExpression.optional(),
    opacity: BasemapExpression.optional(),
    widthPixels: BasemapExpression.optional().meta({
      description: "Lines and outlines only. Defaults to the line's true width.",
    }),
  })
  .meta({ id: "BasemapAppearance" });

export const BasemapPaintStep = z
  .strictObject({
    covers: z
      .union([
        BasemapName,
        z.strictObject({ featureSet: BasemapName, part: z.enum(["fills", "outlines"]) }),
      ])
      .meta({
        description:
          "A raster or a line feature set by name, or a polygon feature set's fills or outlines.",
      }),
    appearance: Appearance,
    blend: z.enum(["over", "multiply"]).default("over"),
  })
  .meta({
    id: "BasemapPaintStep",
    description: "Paint something, in this style, over every step before it.",
  });
export type BasemapPaintStep = z.infer<typeof BasemapPaintStep>;

export const BasemapManifest = z
  .strictObject({
    format: z.literal(BASEMAP_FORMAT),
    geometry: BasemapGeometry,
    display: BasemapDisplay,
    variables: z.record(BasemapName, BasemapVariable),
    rasters: z.record(BasemapName, BasemapRaster),
    waterBodies: z.record(BasemapName, WaterBody),
    featureSets: z.record(BasemapName, BasemapFeatureSet),
    bundles: z.record(BasemapName, Bundle),
    expressions: z.record(BasemapName, BasemapExpression),
    paintOrder: z.array(BasemapPaintStep).max(64),
    annotations: BasemapAnnotations,
    remixOf: Sha256.optional().meta({
      description:
        "The manifest of the version this basemap was copied from, for attribution. Readers never follow it.",
    }),
  })
  .superRefine((manifest, context) => {
    const problem = (path: (string | number)[], message: string) =>
      context.addIssue({ code: "custom", path, message });
    for (const [name, raster] of Object.entries(manifest.rasters)) {
      if (!manifest.bundles[raster.bundle]) {
        problem(["rasters", name, "bundle"], `No bundle is named ${raster.bundle}.`);
      }
    }
    for (const [name, body] of Object.entries(manifest.waterBodies)) {
      if (!manifest.variables[body.level]) {
        problem(["waterBodies", name, "level"], `No variable is named ${body.level}.`);
      }
      if (!manifest.bundles[body.bundle]) {
        problem(["waterBodies", name, "bundle"], `No bundle is named ${body.bundle}.`);
      }
    }
    manifest.paintOrder.forEach((step, index) => {
      const covered =
        typeof step.covers === "string"
          ? manifest.rasters[step.covers] || manifest.featureSets[step.covers]?.geometry === "line"
          : manifest.featureSets[step.covers.featureSet]?.geometry === "polygon";
      if (!covered) {
        problem(
          ["paintOrder", index, "covers"],
          "It covers no raster or feature set of that kind.",
        );
      }
    });
  })
  .meta({
    id: "BasemapManifest",
    description:
      "A published basemap: everything a reader needs to open it, and the hash of every file it uses. Named by its own hash, and never changed.",
  });
export type BasemapManifest = z.infer<typeof BasemapManifest>;

/**
 * A basemap version's files by role, as its version names them: its manifest under `manifest`,
 * then each bundle's tile file as `bundle:<name>` and each feature set's as `features:<name>`.
 */
export function basemapFileRoles(
  manifestSha256: string,
  manifest: BasemapManifest,
): Record<string, string> {
  return {
    manifest: manifestSha256,
    ...Object.fromEntries(
      Object.entries(manifest.bundles).map(([name, bundle]) => [`bundle:${name}`, bundle.tileFile]),
    ),
    ...Object.fromEntries(
      Object.entries(manifest.featureSets).map(([name, set]) => [`features:${name}`, set.tileFile]),
    ),
  };
}
